import AbstractApiService from 'src/services/abstractApiService';
import { api } from 'pages/APIs';
import type {
  SlackIntegration,
  SlackIntegrationCreate,
  SlackIntegrationUpdate,
  SlackListResponse,
  SlackSingleResponse,
  SlackTestResponse,
  SlackDeleteResponse,
} from 'src/@types/slack';

/**
 * API Service for managing Slack channel integrations via doc2-api.
 */
export default class SlackApiService extends AbstractApiService {
  public constructor() {
    super({});
  }

  /**
   * List all Slack integrations for the organization.
   */
  public async list(): Promise<SlackListResponse> {
    return super.get(api.slack_list);
  }

  /**
   * Get a single Slack integration by ID.
   */
  public async getOne(id: string): Promise<SlackSingleResponse> {
    return super.get(`${api.slack_get}${id}`);
  }

  /**
   * Create a new Slack integration.
   */
  public async create(
    data: SlackIntegrationCreate
  ): Promise<SlackSingleResponse> {
    return super.post(api.slack_create, data);
  }

  /**
   * Update an existing Slack integration.
   */
  public async update(
    id: string,
    data: SlackIntegrationUpdate
  ): Promise<SlackSingleResponse> {
    return super.put(`${api.slack_update}${id}`, data);
  }

  /**
   * Delete a Slack integration.
   */
  public async remove(id: string): Promise<SlackDeleteResponse> {
    return super.delete(`${api.slack_delete}${id}`, {});
  }

  /**
   * Test a Slack integration by sending a test message.
   */
  public async test(id: string): Promise<SlackTestResponse> {
    return super.post(`${api.slack_test}${id}/test`, {});
  }

  // =====================================================
  // Alias methods for consistency
  // =====================================================

  /**
   * Get all Slack integrations (alias for list, returns array).
   */
  public async getAll(): Promise<SlackIntegration[]> {
    const result = await this.list();
    return result.data || [];
  }

  /**
   * Get integration by ID (alias for getOne).
   */
  public async getById(id: string): Promise<SlackIntegration> {
    const result = await this.getOne(id);
    return result.data;
  }

  /**
   * Delete integration (alias for remove).
   */
  public async deleteIntegration(id: string): Promise<void> {
    await this.remove(id);
  }

  /**
   * Toggle integration enabled status.
   */
  public async toggle(
    id: string,
    enabled: boolean
  ): Promise<SlackSingleResponse> {
    return this.update(id, { is_enabled: enabled });
  }
}
